import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { requestGetSites } from '../api/site'
import { DEFAULT_MY_SITE_PAGINATION, DEFAULT_SITE_FILTERS } from '../constants/mySiteProvider'

export const MySiteContext = React.createContext({})

export const MySiteProvider = ({ children }) => {
  const [isMySite, setIsMySite] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [sites, setSites] = useState([])
  const [pagination, setPagination] = useState(() => ({ ...DEFAULT_MY_SITE_PAGINATION }))
  const [allSiteLength, setAllSiteLength] = useState(0)
  const [mySiteLength, setMySiteLength] = useState(0)
  const [sitesListRefreshNonce, setSitesListRefreshNonce] = useState(0)
  const [siteFilters, setSiteFilters] = useState(() => ({ ...DEFAULT_SITE_FILTERS }))

  const getSitesCount = useCallback((mySite) => {
    setIsLoading(true)
    const params = {
      resultsPerPage: 12,
      currentPage: 1,
      toggle: mySite
    }

    requestGetSites(params)
      .then((data) => {
        const apiTotal = data?.pagination?.totalNumber || 0
        if (mySite) {
          setMySiteLength(apiTotal)
          return
        }
        setAllSiteLength(apiTotal)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const refreshSiteCounts = useCallback(() => {
    getSitesCount(true)
    getSitesCount(false)
  }, [getSitesCount])

  const refreshSites = useCallback(() => {
    setSitesListRefreshNonce((n) => n + 1)
  }, [])

  const resetFilters = useCallback(() => {
    setPagination({ ...DEFAULT_MY_SITE_PAGINATION })
    setSites([])
    setSiteFilters({ ...DEFAULT_SITE_FILTERS })
  }, [])

  const updateSitesFilters = useCallback((filterId, value) => {
    setSiteFilters((prev) => {
      switch (filterId) {
        case 'year':
          return { ...prev, year: value }
        case 'country':
          return { ...prev, country: value }
        case 'status':
          return { ...prev, status: value }
        default:
          return prev
      }
    })
  }, [])

  const updateFilteredSites = useCallback((data) => {
    setSites(data)
  }, [])

  useEffect(() => {
    getSitesCount(true)
    getSitesCount(false)
  }, [getSitesCount])

  const contextValue = useMemo(
    () => ({
      isMySite,
      setIsMySite,
      allSiteLength,
      mySiteLength,
      setMySiteLength,
      setAllSiteLength,
      getSitesCount,
      updateSitesFilters,
      siteFilters,
      updateFilteredSites,
      sites,
      resetFilters,
      pagination,
      setPagination,
      isLoading,
      setIsLoading,
      refreshSiteCounts,
      refreshSites,
      sitesListRefreshNonce
    }),
    [
      isMySite,
      allSiteLength,
      mySiteLength,
      getSitesCount,
      updateSitesFilters,
      siteFilters,
      updateFilteredSites,
      sites,
      resetFilters,
      pagination,
      isLoading,
      refreshSiteCounts,
      refreshSites,
      sitesListRefreshNonce
    ]
  )

  return <MySiteContext.Provider value={contextValue}>{children}</MySiteContext.Provider>
}

MySiteProvider.propTypes = {
  children: PropTypes.node.isRequired
}
